import React, { createContext, ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router";

import endpoints from "../helpers/endpoints";
import { getNumberStep } from "../helpers/functions";
import useAxios from "../hooks/useAxios";
import { ApplicationType } from "../types/Application";
import { CohabitantType } from "../types/Cohabitant";
import { DwellingType, InitialFormType } from "../types/Form";
import { NoteType } from "../types/Note";
import { PersonType } from "../types/Person";
import { NewRejectedType } from "../types/Rejected";

interface NewRequestContextType {
  loading: boolean;
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  person: PersonType;
  setPerson: React.Dispatch<React.SetStateAction<PersonType>>;
  rejected: NewRejectedType;
  setRejected: React.Dispatch<React.SetStateAction<NewRejectedType>>;
  form: InitialFormType;
  setForm: React.Dispatch<React.SetStateAction<InitialFormType>>;
  cohabitants: CohabitantType[];
  setCohabitants: React.Dispatch<React.SetStateAction<CohabitantType[]>>;
  dwelling: DwellingType;
  setDwelling: React.Dispatch<React.SetStateAction<DwellingType>>;
  note: NoteType;
  setNote: React.Dispatch<React.SetStateAction<NoteType>>;
  application: ApplicationType;
  setApplication: React.Dispatch<React.SetStateAction<ApplicationType>>;
}

const initialRejected: NewRejectedType = {
  description: "",
};

export const NewRequestContext = createContext<NewRequestContextType>({
  loading: true,
  activeStep: 0,
  setActiveStep: () => {},
  person: {} as PersonType,
  setPerson: () => {},
  rejected: initialRejected,
  setRejected: () => {},
  form: {} as InitialFormType,
  setForm: () => {},
  cohabitants: [] as CohabitantType[],
  setCohabitants: () => {},
  dwelling: {} as DwellingType,
  setDwelling: () => {},
  note: {} as NoteType,
  setNote: () => {},
  application: {} as ApplicationType,
  setApplication: () => {},
});

export const NewRequestContextProvider = ({ children }: { children: ReactNode }) => {
  const { idApplication } = useParams();

  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [person, setPerson] = useState<PersonType>({} as PersonType);
  const [rejected, setRejected] = useState<NewRejectedType>(initialRejected);
  const [form, setForm] = useState<InitialFormType>({} as InitialFormType);
  const [cohabitants, setCohabitants] = useState<CohabitantType[]>([]);
  const [dwelling, setDwelling] = useState<DwellingType>({} as DwellingType);
  const [note, setNote] = useState<NoteType>({} as NoteType);
  const [application, setApplication] = useState<ApplicationType>({} as ApplicationType);
  const [actualApp, setActualApp] = useState<null | string>(null);

  const { data: applicationData, refetch: getApplication } = useAxios<ApplicationType | false>({
    url: `${endpoints.getApplicationById}/${actualApp}`,
    awaiting: true,
  });
  const { data: prefillData, refetch: getPrefillData } = useAxios<any>({
    url: `${endpoints.getPrefillDataByPersonId}/${person.id}`,
    awaiting: true,
  });

  useEffect(() => {
    if (idApplication) {
      setLoading(true);
      setActualApp(idApplication);
    } else {
      setLoading(false);
    }
  }, [idApplication]);

  useEffect(() => {
    if (actualApp) {
      getApplication();
    }
  }, [actualApp]);

  useEffect(() => {
    if (person?.id && !idApplication) {
      getPrefillData();
    }
  }, [person?.id, idApplication]);

  useEffect(() => {
    if (applicationData) {
      setPerson(applicationData.person);
      setActiveStep(getNumberStep(applicationData));

      if (applicationData.form) {
        setForm({
          id: applicationData.form.id,
          createdAt: applicationData.form.createdAt,
          updatedAt: applicationData.form.updatedAt,
          address: applicationData.form.address || "",
          maritalStatus: applicationData.form.maritalStatus || "single",
          job: applicationData.form.job,
          workplace: applicationData.form.workplace || "",
          earnings: applicationData.form.earnings ?? 0,
          education: applicationData.form.education || "other",
        });

        setCohabitants(applicationData.form.cohabitants || []);

        setDwelling({
          material: applicationData.form.material || "material",
          roof: applicationData.form.roof || "metal",
          tenure: applicationData.form.tenure || "owned",
          rentAmount: applicationData.form.rentAmount !== null && applicationData.form.rentAmount !== undefined ? applicationData.form.rentAmount : "",
          hasElectricity:
            applicationData.form.hasElectricity !== null && applicationData.form.hasElectricity !== undefined ? applicationData.form.hasElectricity : true,
          waterSupply: applicationData.form.waterSupply || "potable_water",
          heatSource: applicationData.form.heatSource || "gas_stove",
        });
      }

      if (applicationData.note) {
        setNote(applicationData.note);
      } else {
        setNote({} as NoteType);
      }

      setApplication(applicationData);
      setLoading(false);
    }
  }, [applicationData]);

  useEffect(() => {
    if (prefillData && prefillData.hasPreviousApplication && !idApplication && !applicationData) {
      if (prefillData.form) {
        setForm({
          address: prefillData.form.address || "",
          maritalStatus: prefillData.form.maritalStatus || "single",
          job: prefillData.form.job,
          workplace: prefillData.form.workplace || "",
          earnings: prefillData.form.earnings ?? 0,
          education: prefillData.form.education || "other",
        } as InitialFormType);
      }
      if (prefillData.dwelling) {
        setDwelling({
          material: prefillData.dwelling.material || "material",
          roof: prefillData.dwelling.roof || "metal",
          tenure: prefillData.dwelling.tenure || "owned",
          rentAmount: prefillData.dwelling.rentAmount ?? "",
          hasElectricity: prefillData.dwelling.hasElectricity ?? true,
          waterSupply: prefillData.dwelling.waterSupply || "potable_water",
          heatSource: prefillData.dwelling.heatSource || "gas_stove",
        });
      }
      if (prefillData.cohabitants) {
        setCohabitants(prefillData.cohabitants || []);
      }
      if (prefillData.note) {
        setNote({
          recipient: prefillData.note.recipient,
          body: prefillData.note.body,
          date: new Date().toISOString().split("T")[0],
        } as NoteType);
      }
      if (prefillData.application) {
        setApplication({
          ...({} as ApplicationType),
          category: prefillData.application.category,
          problematic: prefillData.application.problematic,
          observation: prefillData.application.observation,
          amount: prefillData.application.amount,
          unit: prefillData.application.unit,
          isAccountable: prefillData.application.isAccountable,
        });
      }
    }
  }, [prefillData, idApplication, applicationData]);

  const value: NewRequestContextType = {
    loading,
    activeStep,
    setActiveStep,
    person,
    setPerson,
    rejected,
    setRejected,
    form,
    setForm,
    cohabitants,
    setCohabitants,
    dwelling,
    setDwelling,
    note,
    setNote,
    application,
    setApplication,
  };

  return <NewRequestContext.Provider value={value}>{children}</NewRequestContext.Provider>;
};
